import React, { useRef, useState, useEffect } from 'react'
import useStore from '../../stores/useStore'
import TextBlock from "../controlsPanel/components/text-block"

const PreviewSectionWidget = () => {
    const {
        backgrounds,
        currentBackgroundId,
        rotation,
        flipX,
        flipY,
        textBlocks,
        selectedBlockId,
        selectTextBlock,
        updateTextBlock,
        removeTextBlock,
        csvData
    } = useStore()

    const containerRef = useRef(null)
    const [scale, setScale] = useState(1)
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 })
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
    const [containerPadding, setContainerPadding] = useState(20) // Добавляем отступы

    const currentBackground = backgrounds.find(bg => bg.id === currentBackgroundId)

    useEffect(() => {
        const updateContainerSize = () => {
            if (containerRef.current) {
                const { width, height } = containerRef.current.getBoundingClientRect()
                setContainerSize({
                    width: Math.max(width - containerPadding * 2, 100),
                    height: Math.max(height - containerPadding * 2, 100)
                })
            }
        }

        updateContainerSize()
        window.addEventListener('resize', updateContainerSize)

        return () => window.removeEventListener('resize', updateContainerSize)
    }, [containerPadding])

    useEffect(() => {
        if (!currentBackground) {
            setImageSize({ width: 0, height: 0 })
            setScale(1)
            return
        }

        const img = new Image()
        img.onload = () => {
            const imgWidth = img.naturalWidth
            const imgHeight = img.naturalHeight

            setImageSize({ width: imgWidth, height: imgHeight })

            const availableWidth = containerSize.width
            const availableHeight = containerSize.height

            if (availableWidth > 0 && availableHeight > 0) {
                const scaleX = availableWidth / imgWidth
                const scaleY = availableHeight / imgHeight
                const newScale = Math.min(scaleX, scaleY, 1)

                setScale(newScale)

                if (newScale < 0.3) {
                    setContainerPadding(10)
                } else {
                    setContainerPadding(20)
                }
            }
        }

        img.onerror = () => {
            console.error('Failed to load image for size calculation')
            setImageSize({ width: 0, height: 0 })
            setScale(1)
        }

        img.src = currentBackground.imageData
    }, [currentBackground, containerSize])

    const transformStyle = {
        transform: `rotate(${rotation}deg) scaleX(${flipX ? -1 : 1}) scaleY(${flipY ? -1 : 1})`,
        transition: 'transform 0.3s ease',
        transformOrigin: 'center center',
    }

    const scaledTextBlocks = textBlocks.map(block => ({
        ...block,
        x: block.x * scale,
        y: block.y * scale,
        fontSize: block.fontSize * scale,
        width: block.width === 'auto' ? 'auto' : block.width * scale,
        height: block.height === 'auto' ? 'auto' : block.height * scale
    }))

    const getPreviewTextBlocks = () => {
        if (!csvData || csvData.length === 0) {
            return textBlocks
        }

        const previewRow = csvData[0]

        return textBlocks.map(block => {
            if (block.key && block.text && previewRow[block.key]) {
                const value = previewRow[block.key]
                const regex = new RegExp(`\\{${escapeRegExp(block.key)}\\}`, 'g')
                return {
                    ...block,
                    text: block.text.replace(regex, String(value))
                }
            }
            return block
        })
    }

    return (
        <section
            className="PreviewSectionWidget"
            style={{
                position: 'relative',
                overflow: 'auto',
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: `${containerPadding}px`
            }}
        >
            <div
                className="PreviewSectionWidget-container"
                ref={containerRef}
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px',
                    background: currentBackground ? 'transparent' : '#f0f0f0',
                    border: currentBackground ? 'none' : '2px dashed #d9d9d9',
                    borderRadius: '8px'
                }}
            >
                {currentBackground && imageSize.width > 0 && imageSize.height > 0 && (
                    <div style={{
                        position: 'relative',
                        width: imageSize.width * scale,
                        height: imageSize.height * scale,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <img
                            src={currentBackground.imageData}
                            alt={currentBackground.name || "Фон"}
                            className="PreviewSectionWidget-background-image"
                            style={{
                                ...transformStyle,
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain', // Важно: сохраняет пропорции
                                maxWidth: '100%',
                                maxHeight: '100%'
                            }}
                        />

                        {/* Текстовые блоки с масштабированными координатами */}
                        {getPreviewTextBlocks().map((block) => {
                            const scaledBlock = {
                                ...block,
                                x: block.x * scale,
                                y: block.y * scale,
                                fontSize: block.fontSize * scale,
                                width: block.width === 'auto' ? 'auto' : block.width * scale,
                                height: block.height === 'auto' ? 'auto' : block.height * scale
                            }

                            return (
                                <TextBlock
                                    key={block.id}
                                    block={scaledBlock}
                                    isSelected={block.id === selectedBlockId}
                                    onSelect={selectTextBlock}
                                    updateTextBlock={(id, updates) => {
                                        const unscaledUpdates = { ...updates }
                                        if (updates.x !== undefined) unscaledUpdates.x = updates.x / scale
                                        if (updates.y !== undefined) unscaledUpdates.y = updates.y / scale
                                        if (updates.fontSize !== undefined) unscaledUpdates.fontSize = updates.fontSize / scale
                                        if (updates.width !== undefined && updates.width !== 'auto') {
                                            unscaledUpdates.width = updates.width / scale
                                        }
                                        if (updates.height !== undefined && updates.height !== 'auto') {
                                            unscaledUpdates.height = updates.height / scale
                                        }
                                        updateTextBlock(id, unscaledUpdates)
                                    }}
                                    removeTextBlock={removeTextBlock}
                                />
                            )
                        })}
                    </div>
                )}

                {/* Информационная панель */}
                {currentBackground && textBlocks.length > 0 && csvData && csvData.length > 0 && (
                    <div style={{
                        position: 'absolute',
                        bottom: '40px',
                        left: '10px',
                        background: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        padding: '8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        zIndex: 5
                    }}>
                        <div>Масштаб превью: {scale.toFixed(2)}x</div>
                        <div>Размер оригинала: {imageSize.width} × {imageSize.height}px</div>
                        <div>Размер превью: {Math.round(imageSize.width * scale)} × {Math.round(imageSize.height * scale)}px</div>
                        <div>Активные ключи: {textBlocks.filter(b => b.key).map(b => b.key).join(', ')}</div>
                        <div>Загружено CSV записей: {csvData.length}</div>
                        <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.8 }}>
                            Показана первая запись из CSV
                        </div>
                    </div>
                )}

                {!currentBackground && (
                    <div style={{
                        padding: '40px',
                        textAlign: 'center',
                        color: '#999'
                    }}>
                        <div style={{ fontSize: '16px', marginBottom: '10px' }}>
                            Загрузите фоновое изображение
                        </div>
                        <div style={{ fontSize: '14px' }}>
                            Перетащите изображение в панель "Фон" или нажмите "Выбрать файлы"
                        </div>
                    </div>
                )}
            </div>

            {currentBackground && backgrounds.length > 1 && (
                <div className="background-badge" style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    zIndex: 10,
                }}>
                    {backgrounds.findIndex(bg => bg.id === currentBackgroundId) + 1} / {backgrounds.length}
                </div>
            )}
        </section>
    )
}

const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export default PreviewSectionWidget